/**

 * 文案类提示语常量
 * 职责: FABE分析、产品描述、品牌故事等文案相关提示语
 */
import type { TemplateType } from '../../../types/ai-analysis-types';

export const COPYWRITING_PROMPTS: Partial<Record<TemplateType, string>> = {
    copywriting: `你是一位精通消费者心理学和神经语言程序学(NLP)的亚马逊资深文案专家。请对以下核心卖点进行深度的 FABE (Features, Advantages, Benefits, Evidence) 分析。

# 核心指令
1. **空值处理**：如果输入的【核心卖点】描述过于简略或部分信息缺失，请基于该品类的通用特性和电商常识进行合理发散和补充，不要直接返回空值。
2. **语言风格**：请使用极具感染力、能够激发购买欲望的营销语言，避免枯燥的技术说明。
3. **本土化**：英文文案必须符合母语者的阅读习惯，避免中式英语。

{{context}}

核心卖点: "{{selling_point}}"

{{script_instruction}}

# 输出要求
请严格按照以下 JSON 格式输出（不要包含 Markdown 代码块标记）：
{
    "feature": {
        "zh": "中文特性描述 (是什么？客观属性)",
        "en": "English Feature Description (What is it?)"
    },
    "advantage": {
        "zh": "中文优势描述 (比别人好在哪里？差异化)",
        "en": "English Advantage Description (Why is it better?)"
    },
    "benefit": {
        "zh": "中文利益描述 (对用户有什么好处？解决什么痛点？)",
        "en": "English Benefit Description (What's in it for me?)"
    },
    "evidence": {
        "zh": "中文证据/证明 (如何证明？数据/证书/背书)",
        "en": "English Evidence/Proof (How to prove it?)"
    }
}`,

    product_description: `你是一位专业的亚马逊 Listing 优化师。请根据以下信息，为产品撰写一段符合 A9 算法且高转化的产品描述 (Product Description)。

# 核心指令
1. **空值处理**：如果【核心卖点】列表为空，请根据【产品名称】推断其主要功能和优势进行撰写。
2. **SEO优化**：请自然地将核心关键词植入文案中，提高搜索权重。
3. **排版优化**：使用 HTML 标签（如 <b>, <br>）进行排版，确保移动端阅读体验。

产品名称: {{product_name}}
核心卖点: {{selling_points}}

# 撰写要求
1. **开头**：用一句话痛点场景切入，瞬间抓住用户注意力。
2. **中间**：详细阐述 3-4 个核心卖点，使用"特性+利益"的结构。
3. **结尾**：包含强有力的行动号召 (Call to Action) 和售后保障承诺。
4. **长度**：控制在 150-250 词之间（英文）。`,

    desired_feeling: `你是一位品牌情感营销专家。请分析以下产品，挖掘用户在使用该产品时深层次的情感需求，给出 3-5 个期望感受关键词。

# 核心指令
1. **空值处理**：如果【产品描述】为空，仅基于【产品名称】进行联想。
2. **情感升维**：不要只停留在"好用"、"方便"等功能层面，要上升到"自信"、"归属感"、"安全感"等心理层面。

产品名称: {{product_name}}
产品描述: {{product_description}}

# 输出要求
1. 关键词要精准、有力，直击人心。
2. 每个关键词附带简短的解释，说明为什么产品能带来这种感受。`,

    brand_story: `你是一位擅长讲故事的品牌主理人。请为以下产品创作一个简短动人、具有品牌温度的品牌故事。

# 核心指令
1. **空值处理**：如果【品牌理念】为空，请根据产品特性构思一个关于"匠心"、"创新"或"用户关怀"的通用品牌故事。
2. **叙事结构**：采用"起（缘起/初心）- 承（挑战/坚持）- 转（突破/创新）- 合（愿景/承诺）"的微型叙事结构。

产品名称: {{product_name}}
品牌理念: {{brand_concept}}

# 撰写要求
1. 故事要有情感共鸣，让用户感受到品牌背后是有血有肉的人。
2. 长度控制在 100-150 词（英文），语言优美流畅。`,

    lifestyle_scene: `你是一位拥有百万粉丝的生活方式博主。请为以下产品描绘 3 个具体、生动的使用场景，让用户产生强烈的代入感。

# 核心指令
1. **空值处理**：如果【目标人群】为空，请推断该产品的典型用户画像（如：忙碌的职场妈妈、追求极致的户外爱好者）。
2. **五感描写**：在场景描述中调动视觉、听觉、触觉等感官描写，增强真实感。

产品名称: {{product_name}}
目标人群: {{target_audience}}

# 撰写要求
1. **场景一（痛点解决）**：描述用户在遇到麻烦时，产品如何轻松解决问题。
2. **场景二（愉悦体验）**：描述用户在使用产品时享受到的美好时光。
3. **场景三（社交分享）**：描述用户向朋友推荐产品时的自豪感。`
};

/**
 * 智能文案系统专用提示语模板
 * 用于：运营策略 → SKU详情 → 智能文案
 */
export const SMART_COPYWRITING_PROMPTS = {
    /** Step 2: 目标人群智能分析 */
    smart_audience_analysis: `你是一位资深的亚马逊运营专家和消费者心理学专家。请根据以下产品信息，分析并推荐尽可能多的目标人群。

产品名称: {{product_name}}

核心卖点:
{{selling_points}}

品牌调性: {{brand_tone}}
用户自定义人群方向: {{custom_input}}

# 任务要求
1. **通用人群**: 必须包含1-2个适用于所有站点的广泛受众群体
2. **特定人群**: 尽可能多地推荐具体的细分人群（建议8-15个）
3. **人群维度**: 从以下角度挖掘人群
   - 职业特征（如：程序员、设计师、销售人员）
   - 生活方式（如：健身爱好者、户外运动者、宅家达人）
   - 人生阶段（如：学生党、新手父母、退休人士）
   - 消费特征（如：品质控、性价比党、科技发烧友）
   - 使用场景（如：商旅人士、远程办公者、游戏玩家）

# 剧本类型说明
- blue: 蓝色剧本 - 适合理性决策者，强调数据、参数、认证
- green: 绿色剧本 - 适合感性决策者，强调体验、情感、生活方式
- hybrid: 混合剧本 - 综合运用

# 输出格式（JSON）
{
    "audiences": [
        {
            "name": "人群名称",
            "category": "通用|特定",
            "description": "人群画像详细描述（50-100字）",
            "pain_points": ["痛点1", "痛点2", "痛点3"],
            "purchase_motivation": "购买动机",
            "recommended_script": "blue|green|hybrid",
            "keywords": ["关键词1", "关键词2"]
        }
    ]
}`,

    /** Step 3: 使用场景智能推荐 */
    smart_scenario_recommendation: `你是一位擅长场景营销的亚马逊运营专家。请根据已选择的目标人群，推荐尽可能多的使用场景。

产品名称: {{product_name}}

已选择的目标人群:
{{selected_audiences}}

# 任务要求
1. **场景数量**: 为每个人群推荐3-5个场景，总共推荐15-25个场景
2. **场景维度**: 从以下角度挖掘场景
   - 时间维度（早晨、午休、下班后、周末、节假日）
   - 空间维度（家里、办公室、通勤途中、咖啡店、健身房、户外）
   - 活动维度（工作、休闲、运动、社交、学习、旅行）
   - 情绪维度（放松、专注、兴奋、安静）
3. **场景描写**: 使用五感描写，让场景栩栩如生
4. **关联人群**: 标注每个场景适合哪些人群

# 输出格式（JSON）
{
    "scenarios": [
        {
            "name": "场景名称（简洁）",
            "icon": "合适的Emoji图标",
            "description": "场景描述（100-150字，包含五感元素）",
            "target_audiences": ["人群名称1", "人群名称2"],
            "emotional_keywords": ["情感关键词1", "情感关键词2", "情感关键词3"],
            "recommended_script": "blue|green|hybrid",
            "copywriting_angle": "这个场景下的文案切入点"
        }
    ]
}`,

    /** Step 4: 智能文案生成 */
    smart_copy_generation: `你是一位精通消费者心理学和亚马逊A9算法的资深文案专家。请根据以下信息，为产品生成高转化率的标题和卖点文案。

# 核心卖点（中文）
{{selling_points}}

# 目标站点关键词（必须有机融入文案）
{{keywords_by_site}}

# 目标人群: {{selected_audiences}}
# 使用场景: {{selected_scenarios}}

品牌调性: {{brand_tone}}
品牌故事: {{brand_story}}

# FABE文案框架
每个卖点都需要进行FABE分析:
- Feature (特性): 产品是什么？客观属性
- Advantage (优势): 比竞品好在哪里？差异化
- Benefit (利益): 对用户有什么好处？解决什么痛点？
- Evidence (证据): 如何证明？数据/认证/背书

# 剧本逻辑
【蓝色剧本 - 理性信任型】
- 语气: 专业、客观、严谨
- 适用: 高客单价、功能性强产品
- 关键词: 经认证、数据表明、专利技术、耐用

【绿色剧本 - 感性愉悦型】
- 语气: 热情、生动、有感染力
- 适用: 生活方式品、礼品、冲动消费品
- 关键词: 享受、舒适、惊喜、改变生活

# 输出要求
1. **标题**: 每个站点一个，≤200字符，核心关键词前置
2. **Bullet Points**: 数量 = 卖点数量，每条≤500字符
3. **关键词嵌入**: 尽可能自然地将关键词融入文案
4. **本地化**: 非英语站点使用地道的本地语言，不要翻译腔

# 输出格式（JSON）
{
    "titles": {
        "US": "English title here...",
        "DE": "German title here..."
    },
    "bullet_points": {
        "US": ["Bullet point 1...", "Bullet point 2..."],
        "DE": ["Deutscher Aufzählungspunkt 1...", "..."]
    },
    "fabe_analysis": [
        {
            "selling_point": "原始中文卖点",
            "script_used": "blue|green|hybrid",
            "fabe": {
                "feature": {"zh": "中文特性", "en": "English Feature"},
                "advantage": {"zh": "中文优势", "en": "English Advantage"},
                "benefit": {"zh": "中文利益", "en": "English Benefit"},
                "evidence": {"zh": "中文证据", "en": "English Evidence"}
            }
        }
    ]
}`
};
